import { LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from '../components/Feedback';

/**
 * Page de connexion.
 *
 * Le logo Clippit est un lettrage BLANC sur fond transparent. Posé au centre de
 * la carte blanche, comme il l'était, le mot « CLIPPIT » disparaissait purement
 * et simplement et il ne restait que ses deux éclats verts. Il vit donc sur le
 * fond marine, au-dessus de la carte : c'est son usage prévu, et le seul
 * contexte où il se lit.
 *
 * Même raison pour le vert de marque, qui plafonne à 1,37:1 sur blanc et atteint
 * 11,97:1 sur le marine. Voir l'en-tête d'index.css.
 *
 * La page ne suit pas le thème : c'est un aplat de marque, marine et carte
 * blanche dans les deux cas. Ses couleurs sont donc les constantes de marque,
 * jamais les jetons de surface, qui basculeraient avec le mode sombre et
 * feraient disparaître le titre marine sur la carte.
 */
export function LoginPage() {
    const { login, isLoading, error } = useAuth();

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-[var(--color-navy)] relative overflow-hidden">
            {/* Signature de la page : la tabulation du logo, agrandie et sortie
                du cadre. Une seule forme franche plutôt que des halos flous, qui
                sont le fond d'écran par défaut de n'importe quelle page. */}
            <svg
                className="pointer-events-none absolute -top-24 -right-32 w-[34rem] h-[34rem] text-[var(--color-green)] opacity-[0.06]"
                viewBox="0 0 100 100"
                fill="currentColor"
                aria-hidden
            >
                <path d="M0 0 H100 V44 C100 74 62 90 0 100 Z" />
            </svg>

            <main className="relative w-full max-w-sm">
                <img
                    src="/logo-clippit.png"
                    alt="Clippit"
                    className="h-11 w-auto mx-auto logo-wiggle"
                />

                <div className="mt-8 bg-white rounded-2xl shadow-2xl p-8 text-center">
                    <h1 className="text-2xl font-bold text-[var(--color-navy)]">Clippit Hub</h1>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                        Toutes les applications de Clippit, une seule porte d’entrée.
                    </p>

                    {error && (
                        <div
                            className="mt-5 flex items-start gap-2 p-3 rounded-lg bg-red-50 text-left"
                            role="alert"
                        >
                            <AlertCircle
                                className="w-4 h-4 text-red-600 shrink-0 mt-0.5"
                                aria-hidden
                            />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={() => void login()}
                        disabled={isLoading}
                        className="btn btn-brand w-full mt-6 py-2.5"
                    >
                        {isLoading ? (
                            <Spinner className="w-4 h-4 text-current" />
                        ) : (
                            <LogIn className="w-4 h-4" aria-hidden />
                        )}
                        Se connecter avec Microsoft
                    </button>
                </div>

                <p className="mt-5 text-center text-xs text-white/50">
                    Réservé aux collaborateurs de Clippit.
                </p>
            </main>
        </div>
    );
}
